import { Editor, Extension, findChildren, findChildrenInRange } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import _ from 'lodash';

import retry from '../../../../utils/retry';
import apiFetch from 'react-scale/lib/api-fetch';

import { Match, TextRange, LanguageToolResponse } from './typings';

// TODO: Remove unused languages.
const languagesMapToSupportedLanguageLongCode: Record<string, string> = {
  // Tier 1. Batch 1
  German: 'de-DE',
  Spanish: 'es-ES',
  Portuguese: 'pt-PT',
  English: 'en-US',
  // Vietnamese: 'vi-VN', // Nope
  // Indonesian: 'id-ID', // Nope

  // Tier 1. Batch 2
  // 'Hindi (hi-IN)': 'hi-IN', // Nope
  // 'Hindi (hi-Latn)': 'hi-Latn', // Nope
  // 'Hindi (Romanized)': 'hi-Latn', // Dunno if it's like this // Nope
  // Thai: 'th-TH', // Nope
  French: 'fr-FR',
  Italian: 'it-IT',

  // Tier 2. Batch 1
  Japanese: 'ja-JP',
  // Korean: 'ko-KR', // Nope
  Arabic: 'ar',
  // Bengali: 'bn-BD', // Nope
  Chinese: 'zh-CN',

  // Tier 2. Batch 2
  // Marathi: 'mr-IN', // Nope
  Tagalog: 'tl-PH',
  Khmer: 'km-KH',
  // Finnish: 'fi-FI', // Nope
  // Gujarati: 'gu-IN', // Nope
  // Kannada: 'kn-IN', // Nope

  // Tier 2. Batch 3
  // Malayalam: 'ml-IN', // Nope
  // Punjabi: 'pa-IN', // Nope
  Russian: 'ru-RU',
  // Swahili: 'sw-TZ', // Nope
  Tamil: 'ta-IN',
  // Telugu: 'te-IN', // Nope

  // Tier 2. Batch 4
  // Turkish: 'tr-TR', // Nope
  // Urdu: 'ur-PK', // Nope
  // Czech: 'cs-CZ', // Nope
  Dutch: 'nl-NL',
  Greek: 'el-GR',
  // Hungarian: 'hu-HU', // Nope

  // Tier 2. Batch 5
  Persian: 'fa-IR',
  Polish: 'pl-PL',
  Romanian: 'ro-RO',
  Swedish: 'sv-SE',
  // Hebrew: 'he-IL', // Nope
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    languagetool: {
      /**
       * Proofreads whole document
       */
      proofread: () => ReturnType;

      ignoreLanguageToolSuggestion: (match: MatchSelection) => ReturnType;

      toggleLanguageTool: (on: boolean) => ReturnType;

      resetLanguageToolMatch: () => ReturnType;

      bulkIgnoredWords: (words: string[]) => ReturnType;

      setLanguage: (language: string) => ReturnType;
    };
  }
}

export interface LanguageToolOptions {
  language: string;
  active: boolean;
}

export interface LanguageToolStorage {
  loading: boolean;
  errors: MatchSelection[];
  ignoredWords: string[];
  selectedMatch: MatchSelection | null;
}

export interface MatchSelection {
  match: Match;
  range: TextRange;
}

enum Transactions {
  SelectedMatchUpdated = 'selectedMatchUpdated',
  LoadingUpdated = 'languageToolLoading',
  ErrorsUpdated = 'languageToolError',
  IgnoredWordsUpdated = 'languageToolIgnoredWords',
}

export function LanguageTool() {
  let editor: Editor;
  let editorView: EditorView;
  let decorationSet: DecorationSet; // Decoration set for managing decorations

  let language = ''; // API language
  let errors: MatchSelection[] = []; // Grammar errors
  let ignoredWords: Set<string> = new Set(); // Ignored words

  const controllerPerNode = new Map<string, AbortController>();

  const updateErrors = () => {
    editorView.dispatch(editorView.state.tr.setMeta(Transactions.ErrorsUpdated, errors));
  };

  const debouncedUpdateErrors = _.debounce(updateErrors, 300);

  const updateIgnoredWords = () => {
    editorView.dispatch(
      editorView.state.tr.setMeta(Transactions.IgnoredWordsUpdated, ignoredWords),
    );
  };

  // Update match and range, then dispatch a transaction to update the state
  const updateSelectedMatch = (match: MatchSelection | null) => {
    editorView.dispatch(editorView.state.tr.setMeta(Transactions.SelectedMatchUpdated, match));
  };

  let loadingNodes = 0;

  function updateLoadingIndicator() {
    if (loadingNodes > 0) {
      editorView.dispatch(editorView.state.tr.setMeta(Transactions.LoadingUpdated, true));
    } else {
      editorView.dispatch(editorView.state.tr.setMeta(Transactions.LoadingUpdated, false));
    }
  }

  const debounceTimeouts = new Map<string, NodeJS.Timeout>();

  const debounce = <T extends (...args: any[]) => any>(func: T, delay: number, nodeId: string) => {
    return function (...args: any[]) {
      const existingTimeout = debounceTimeouts.get(nodeId);

      if (existingTimeout !== undefined) {
        clearTimeout(existingTimeout);
      }

      const timeout = setTimeout(() => {
        func.apply(func, args);
        debounceTimeouts.delete(nodeId);
      }, delay);

      debounceTimeouts.set(nodeId, timeout);
    };
  };

  const errorsPerNode = new Map<string, MatchSelection[]>();

  function updateDecorationsFromErrors(doc: PMNode) {
    const decorations: Decoration[] = [];

    errors = [];

    errorsPerNode.forEach((value, nodeId) => {
      const [result] = findChildren(doc, node => node.attrs.id === nodeId);

      if (!result) {
        // eslint-disable-next-line no-console
        console.warn('Errors for node that does not exist in the document');
        return;
      }

      const offset = result.pos + 1;

      const updatedErrors = value.map(error => ({
        ...error,
        range: { ...error.range, offset },
      }));

      // update errors and positions
      errors.push(...updatedErrors);
      decorations.push(...updatedErrors.map(selection => gimmeDecoration(selection)));
    });

    decorationSet = DecorationSet.create(editorView.state.doc, decorations);
  }

  // Get matches from LanguageTool and set decorations in the document
  const getMatchAndSetDecorations = async (node: PMNode, text: string, offset: number) => {
    if (!language || !text.trim()) return;

    const nodeId: string = node.attrs.id;

    controllerPerNode.get(nodeId)?.abort();

    const controller = new AbortController();
    controllerPerNode.set(nodeId, controller);

    try {
      loadingNodes++;
      updateLoadingIndicator();

      const { matches } = await getMatches({
        text,
        language,
        signal: controller.signal,
      });

      const currentErrors: MatchSelection[] = [];

      for (const match of matches) {
        const content = text.substring(match.offset, match.offset + match.length);

        if (!ignoredWords.has(content)) {
          currentErrors.push({
            match,
            range: {
              from: match.offset,
              to: match.offset + match.length,
              offset,
            },
          });
        }
      }

      errorsPerNode.set(nodeId, currentErrors);

      updateDecorationsFromErrors(editorView.state.doc);
      updateErrors();
    } catch (error) {
      const isAbortError =
        _.isError(error) && (error.name === 'AbortError' || error.name === 'CanceledError');

      // if it's an abort error, ignore it
      if (isAbortError) return;

      // eslint-disable-next-line no-console
      console.error('Error getting matches', error);
    } finally {
      loadingNodes--;
      updateLoadingIndicator();
    }
  };

  const debouncedGetMatchAndSetDecorations = (node: PMNode, text: string, from: number) => {
    debounce(getMatchAndSetDecorations, 300, node.attrs.id as string)(node, text, from);
  };

  function deleteNodeAndRemoveDecorations(nodeId: string) {
    // delete the errors for the node
    errorsPerNode.delete(nodeId);

    // abort the controller if it exists
    controllerPerNode.get(nodeId)?.abort();

    // update errors and decorations
    updateDecorationsFromErrors(editorView.state.doc);

    // dispatch a transaction to update the state
    debouncedUpdateErrors();
  }

  const nodeTexts = new Map<string, string>();

  return Extension.create<LanguageToolOptions, LanguageToolStorage>({
    name: 'languagetool',

    addOptions() {
      return {
        active: true,
        language: 'auto',
      };
    },

    addStorage() {
      return {
        loading: false,
        errors: [],
        ignoredWords: [],
        selectedMatch: null,
      };
    },

    addProseMirrorPlugins() {
      const { language: optionsLanguage } = this.options;

      language = optionsLanguage;
      editor = this.editor;

      return [
        new Plugin<{
          decorators: DecorationSet;
        }>({
          key: new PluginKey('languagetoolPlugin'),
          props: {
            decorations(state) {
              return this.getState(state)?.decorators;
            },
            attributes: {
              spellcheck: 'false', // Stops OS spellchecker
            },
            handleDOMEvents: {
              click: view => {
                const { state } = view;
                const { selection } = state;
                const { from, to } = selection;

                const decorations = decorationSet.find(from, to);

                if (decorations.length > 0) {
                  const selection = decorations[0].spec.match as MatchSelection;

                  updateSelectedMatch(selection);

                  // select the text
                  editor.commands.setTextSelection({
                    from: selection.range.from + selection.range.offset,
                    to: selection.range.to + selection.range.offset,
                  });
                } else {
                  updateSelectedMatch(null);
                }

                return false;
              },
              blur: () => {
                setTimeout(() => updateSelectedMatch(null), 10);
              },
            },
          },
          state: {
            init: (_config, state) => {
              decorationSet = DecorationSet.create(state.doc, []);

              return { decorators: decorationSet };
            },
            apply: (tr, _state, oldState, newState) => {
              // Update storage loading
              const loading = tr.getMeta(Transactions.LoadingUpdated);

              if (loading !== undefined) {
                this.storage.loading = loading;
              }

              // Update selected match
              const selected = tr.getMeta(Transactions.SelectedMatchUpdated);

              if (selected !== undefined) {
                this.storage.selectedMatch = selected;
              }

              if (this.storage.selectedMatch) {
                const range = this.storage.selectedMatch.range;
                const selection = editorView.state.selection;

                const inRange =
                  range.from + range.offset <= selection.from &&
                  selection.to <= range.to + range.offset;

                if (!inRange) {
                  updateSelectedMatch(null);
                }
              }

              // update errors
              const errors = tr.getMeta(Transactions.ErrorsUpdated);

              if (errors !== undefined) {
                this.storage.errors = errors;
              }

              // update ignored words
              const ignoredWords = tr.getMeta(Transactions.IgnoredWordsUpdated);

              if (ignoredWords !== undefined) {
                this.storage.ignoredWords = [...ignoredWords];
              }

              // in case the plugin is not active, return an empty decoration set and set empty errors
              if (!this.options.active) {
                this.storage.errors = [];
                return { decorators: DecorationSet.empty };
              }

              if (tr.docChanged) {
                const changes: {
                  node: PMNode;
                  pos: number;
                  previousText: string | undefined;
                  currentText: string;
                }[] = [];

                const deletedNodes: {
                  id: string;
                  node: PMNode;
                  pos: number;
                  previousText: string | undefined;
                }[] = [];

                tr.steps.forEach(step => {
                  const map = step.getMap();

                  oldState.doc.descendants((node, pos) => {
                    const nodeId: string = node.attrs.id;

                    // ignore nodes without an id
                    if (!node.attrs.id) return false;

                    if (node.isTextblock) {
                      const newPos = tr.mapping.mapResult(pos).pos;
                      if (!newState.doc.nodeAt(newPos) || tr.mapping.mapResult(pos).deleted) {
                        deletedNodes.push({
                          node,
                          pos,
                          previousText: nodeTexts.get(nodeId),
                          id: nodeId,
                        });

                        nodeTexts.delete(nodeId);
                      }

                      return false;
                    }
                  });

                  map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
                    const children = findChildrenInRange(
                      newState.doc,
                      { from: newStart, to: newEnd },
                      node => node.isTextblock || node.isBlock,
                    );

                    // take children and look for text blocks
                    // since could be updated the structure and not the text
                    // so we need to look for the text blocks inside the updated blocks
                    children
                      .map(({ node, pos }) =>
                        node.isTextblock
                          ? [{ node, pos }]
                          : findChildren(node, node => node.isTextblock),
                      )

                      // flatten the array
                      .flat()

                      // iterate over the text blocks
                      .forEach(({ node, pos }) => {
                        const nodeId: string = node.attrs.id;

                        // ignore nodes without an id
                        if (!node.attrs.id) {
                          return false;
                        }

                        // stop children recursion if the node is a code block
                        if (node.type.name === 'codeBlock') {
                          return false;
                        }

                        // if the node is a text block, check if the text has changed
                        if (node.isTextblock) {
                          const previousText = nodeTexts.get(nodeId);
                          const currentText = getTextFromTextBlockNode(node);

                          nodeTexts.set(nodeId, currentText);

                          changes.push({
                            node,
                            pos,
                            previousText,
                            currentText,
                          });

                          return false;
                        }
                      });
                  });
                });

                deletedNodes.forEach(({ id: nodeId }) => {
                  deleteNodeAndRemoveDecorations(nodeId);
                });

                changes.forEach(change => {
                  const { node, pos, currentText, previousText } = change;

                  const hasChanged = currentText !== previousText;

                  if (hasChanged) {
                    debouncedGetMatchAndSetDecorations(node, currentText, pos + 1);
                  }
                });

                decorationSet = decorationSet.map(tr.mapping, tr.doc);
              }

              return { decorators: decorationSet };
            },
          },
          view: () => ({
            update: view => {
              editorView = view as any;
            },
          }),
        }),
      ];
    },

    addCommands() {
      return {
        proofread:
          () =>
          ({ state: { doc } }) => {
            if (!this.options.active) {
              // eslint-disable-next-line no-console
              console.warn('LanguageTool is not active');
              return false;
            }

            doc.descendants((node, pos) => {
              if (!node.isTextblock || !node.attrs.id) return;

              const nodeId: string = node.attrs.id;

              const previousText = nodeTexts.get(nodeId);
              const currentText = getTextFromTextBlockNode(node);

              if (previousText !== currentText) {
                getMatchAndSetDecorations(node, currentText, pos + 1);
              }

              return false;
            });

            return true;
          },
        ignoreLanguageToolSuggestion:
          (match: MatchSelection) =>
          ({ editor }) => {
            const { doc } = editor.state;
            const { from, to, offset } = match.range;

            const node = getTextBlockNode(doc, from + offset);

            if (!node || !node.attrs.id) return false;

            const nodeId: string = node.attrs.id;

            const currentErrors = errorsPerNode.get(nodeId) ?? [];

            const updatedErrors = currentErrors.filter(error => error.match !== match.match);

            errorsPerNode.set(nodeId, updatedErrors);

            ignoredWords = new Set(ignoredWords).add(doc.textBetween(from + offset, to + offset));

            updateDecorationsFromErrors(doc);
            updateIgnoredWords();
            debouncedUpdateErrors();

            return false;
          },
        resetLanguageToolMatch: () => () => {
          updateSelectedMatch(null);

          return false;
        },
        toggleLanguageTool:
          (active: boolean) =>
          ({ commands }) => {
            this.options.active = active;

            if (this.options.active) {
              commands.proofread();
            } else {
              errorsPerNode.clear();
              errors = [];

              controllerPerNode.forEach(controller => controller.abort());
              controllerPerNode.clear();

              decorationSet = DecorationSet.empty;

              updateSelectedMatch(null);
              updateErrors();
            }

            return false;
          },

        bulkIgnoredWords: (words: string[]) => () => {
          ignoredWords = new Set([...ignoredWords, ...words]);
          editor.storage.ignoredWords = [...ignoredWords];

          return false;
        },

        setLanguage: (lang: string) => () => {
          this.options.language = lang;
          language = lang;

          return false;
        },
      };
    },
  });
}

/**
 * Get the supported language long code from the language name.
 */
export function getLanguage(language: string) {
  return languagesMapToSupportedLanguageLongCode[language];
}

async function getMatches({
  text,
  language,
  signal,
}: {
  text: string;
  language: string;
  signal: AbortSignal;
}) {
  const { matches } = await retry(
    () =>
      apiFetch<LanguageToolResponse>(null, `internal/genai/languageTool`, {
        body: { text, language },
        maxRetries: 2,
        method: 'POST',
        parseAndHandle: true,
        redirectOnUnauthorized: true,
        signal,
      }),
    {
      type: 'exponential',
      limit: 5,
    },
  );

  return { matches };
}

// Create a decoration for a match
const gimmeDecoration = (selection: MatchSelection) =>
  Decoration.inline(
    selection.range.from + selection.range.offset,
    selection.range.to + selection.range.offset,
    {
      class: `grammar-checker grammar-checker-${selection.match.rule.issueType} underline decoration-danger-500 decoration-wavy underline-offset-4 cursor-default`,
      nodeName: 'span',
    },
    {
      match: selection,
    },
  );

function getTextBlockNode(doc: PMNode, pos: number): PMNode | null {
  const resolvedPos = doc.resolve(pos);

  for (let depth = resolvedPos.depth; depth >= 0; depth--) {
    const node = resolvedPos.node(depth);
    if (node.isTextblock) {
      return node;
    }
  }

  return null;
}

function getTextFromTextBlockNode(node: PMNode) {
  if (!node.isTextblock) return '';

  let text = '';

  node.descendants(child => {
    if (child.isText) {
      text += child.text;
    } else if (child.type.name === 'hardBreak') {
      text += '\n';
    }
  });

  return text;
}
